import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Popper from "@mui/material/Popper";

// long_term_memo lives in item_attributes["all#custom_data"] and comes as a boolean
export const isLongTermMemo = (row) => {
  const customData = row?.item_attributes?.["all#custom_data"];
  const ltm = customData?.ori?.long_term_memo ?? customData?.trans?.long_term_memo;
  return ltm === true || String(ltm).toLowerCase() === "true";
};

// Same yellow as the "No matching ... found" alert
export const longTermMemoRowStyles = [
  {
    when: isLongTermMemo,
    style: { backgroundColor: "#fff3cd" },
  },
];

// Holds its own position state so cursor moves re-render only the tooltip,
// not the report/table that renders it.
const LongTermMemoTooltip = React.memo(({ controllerRef }) => {
  const [position, setPosition] = useState(null);
  // True only while the cursor is over a long-term-memo row. A ref (not state) so a
  // mousemove fired right after leaving the row can't reopen the tooltip.
  const hoveringRef = useRef(false);

  useEffect(() => {
    controllerRef.current = {
      show: (x, y) => {
        hoveringRef.current = true;
        setPosition({ x, y });
      },
      hide: () => {
        hoveringRef.current = false;
        setPosition(null);
      },
    };
    return () => {
      controllerRef.current = null;
    };
  }, [controllerRef]);

  const isOpen = position !== null;

  // Follow the cursor while the tooltip is open
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleMove = (e) => {
      if (hoveringRef.current) setPosition({ x: e.clientX, y: e.clientY });
    };
    document.addEventListener("mousemove", handleMove);
    return () => document.removeEventListener("mousemove", handleMove);
  }, [isOpen]);

  const anchorEl = useMemo(
    () =>
      position && {
        getBoundingClientRect: () => ({
          width: 0,
          height: 0,
          top: position.y,
          bottom: position.y,
          left: position.x,
          right: position.x,
          x: position.x,
          y: position.y,
        }),
      },
    [position]
  );

  return (
    <Popper
      open={isOpen}
      anchorEl={anchorEl}
      placement="top-start"
      modifiers={[{ name: "offset", options: { offset: [8, 12] } }]}
      style={{ zIndex: 1500, pointerEvents: "none" }}
    >
      <div className="bg-[#616161e6] text-white text-[0.68rem] font-medium rounded px-2 py-1 whitespace-nowrap">
        Long Term Memo
      </div>
    </Popper>
  );
});

// Row highlight + hover tooltip for react-data-table-component.
// Spread `tableProps` on <DataTable> and render `tooltip` anywhere in the tree.
const useLongTermMemoRow = () => {
  const controllerRef = useRef(null);

  const onRowMouseEnter = useCallback((row, e) => {
    if (isLongTermMemo(row)) controllerRef.current?.show(e.clientX, e.clientY);
    else controllerRef.current?.hide();
  }, []);

  const onRowMouseLeave = useCallback(() => controllerRef.current?.hide(), []);

  const tableProps = useMemo(
    () => ({
      conditionalRowStyles: longTermMemoRowStyles,
      onRowMouseEnter,
      onRowMouseLeave,
    }),
    [onRowMouseEnter, onRowMouseLeave]
  );

  const tooltip = useMemo(() => <LongTermMemoTooltip controllerRef={controllerRef} />, []);

  return { tableProps, tooltip };
};

export default useLongTermMemoRow;
